import * as React from 'react';
import { cn } from '@common/bw-ui/lib/utils';

interface FormFieldContextValue {
  id: string;
  describedBy?: string;
  invalid: boolean;
}

const FormFieldContext = React.createContext<FormFieldContextValue | null>(
  null,
);

/**
 * Form controls (Input, Select, PasswordInput, …) call this to inherit the
 * enclosing FormField's control id and aria wiring. Explicit props on the
 * control always win over the context values.
 */
export function useFormControl<
  P extends {
    id?: string;
    'aria-describedby'?: string;
    'aria-invalid'?: React.AriaAttributes['aria-invalid'];
  },
>(props: P) {
  const field = React.useContext(FormFieldContext);
  return {
    id: props.id ?? field?.id,
    'aria-describedby': props['aria-describedby'] ?? field?.describedBy,
    'aria-invalid': props['aria-invalid'] ?? (field?.invalid || undefined),
  };
}

const Label = React.forwardRef<
  HTMLLabelElement,
  React.LabelHTMLAttributes<HTMLLabelElement>
>(({ className, ...props }, ref) => (
  <label
    ref={ref}
    className={cn(
      'tw:block tw:text-sm tw:font-medium tw:text-foreground',
      className,
    )}
    {...props}
  />
));
Label.displayName = 'Label';

export interface FormFieldProps {
  label: React.ReactNode;
  /** Appends the design's literal " (Required)" / " (Optional)" label suffix */
  requirement?: 'required' | 'optional';
  /** Hint text rendered between the label and the control */
  description?: React.ReactNode;
  /**
   * Error line below the control; also marks the control aria-invalid via
   * context, which gives it the red invalidated styling
   */
  error?: React.ReactNode;
  /** Control id; generated when omitted. The control inherits it via context */
  id?: string;
  className?: string;
  children: React.ReactNode;
}

function FormField({
  label,
  requirement,
  description,
  error,
  id: idProp,
  className,
  children,
}: FormFieldProps) {
  const generatedId = React.useId();
  const id = idProp ?? generatedId;
  const descriptionId = description ? `${id}-description` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy =
    [descriptionId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div
      data-slot="form-field"
      className={cn('tw:flex tw:flex-col tw:gap-2', className)}
    >
      <Label htmlFor={id}>
        {label}
        {requirement === 'required' && ' (Required)'}
        {requirement === 'optional' && ' (Optional)'}
      </Label>
      {description && (
        <p
          id={descriptionId}
          data-slot="form-description"
          className="tw:m-0 tw:text-sm tw:font-normal tw:text-foreground"
        >
          {description}
        </p>
      )}
      <FormFieldContext.Provider
        value={{ id, describedBy, invalid: Boolean(error) }}
      >
        {children}
      </FormFieldContext.Provider>
      {error && (
        <p
          id={errorId}
          data-slot="form-error"
          role="alert"
          className="tw:m-0 tw:text-sm tw:font-normal tw:text-destructive"
        >
          {error}
        </p>
      )}
    </div>
  );
}

export { FormField, Label };
